import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { ClientDto, CustomerDto, MaterialDto, PagedResult } from '@/types/materials';
import type {
  CreateDispatchOrderRequest,
  DispatchOrderDto,
  DispatchOrderLineDto,
  DispatchOrderLineInput,
  UpdateDispatchOrderRequest,
} from '@/types/dispatch';

const EMPTY_LINE: DispatchOrderLineInput = { materialId: '', requestedQty: 0 };

export default function DispatchOrdersPage() {
  const { t } = useTranslation('dispatch');
  const queryClient = useQueryClient();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['dispatch-orders'],
    queryFn: async () => (await apiClient.get<DispatchOrderDto[]>('/dispatch-orders')).data,
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => (await apiClient.get<CustomerDto[]>('/customers')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: processConfiguration } = useProcessConfiguration();
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: processConfiguration?.thirdPartyLogisticsEnabled ?? false,
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<DispatchOrderDto | null>(null);
  // Which destination(s) this company's Dispatch Order screen offers — Process Configuration's
  // Dispatch Type, independent of Weigh & Label (see DispatchOrderService.ResolveDestination for
  // the server-side mirror of this gate). Defaults to 'Customer' until the config has loaded,
  // matching this build's original, non-configurable behavior.
  const dispatchTypeScope = processConfiguration?.dispatchTypeScope ?? 'Customer';
  const showCustomerOption = dispatchTypeScope !== 'Warehouse';
  const showWarehouseOption = dispatchTypeScope !== 'Customer';
  const [destinationType, setDestinationType] = useState<'Customer' | 'Warehouse'>('Customer');
  const [customerId, setCustomerId] = useState('');
  const [destinationWarehouseId, setDestinationWarehouseId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [clientId, setClientId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<DispatchOrderLineInput[]>([EMPTY_LINE]);

  // Once 3PL is on, materials are scoped to the selected Customer+Client pair — the picker stays
  // empty until both are chosen (mirrors PutawayPage's queryKey-includes-upstream-id +
  // enabled: Boolean(...) convention, extended to two upstream ids here).
  const thirdPartyLogisticsEnabled = processConfiguration?.thirdPartyLogisticsEnabled ?? false;
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker', thirdPartyLogisticsEnabled ? clientId : null, thirdPartyLogisticsEnabled ? customerId : null],
    queryFn: async () =>
      (
        await apiClient.get<PagedResult<MaterialDto>>('/materials', {
          params: {
            page: 1, pageSize: 200,
            clientId: thirdPartyLogisticsEnabled ? clientId || undefined : undefined,
            customerId: thirdPartyLogisticsEnabled ? customerId || undefined : undefined,
          },
        })
      ).data,
    enabled: !thirdPartyLogisticsEnabled || (Boolean(clientId) && Boolean(customerId)),
  });
  const materials = materialsPage?.items ?? [];
  // Process Configuration's Dispatch Material Scope decides whether this picker offers only
  // Finished Goods (the normal customer-facing default) or every material — defaults to FG-only
  // (matching this build's original, non-configurable behavior) until the config has loaded. A
  // pure 3PL warehouse with the whole Production module off never manufactures anything, so the
  // FG/RM/SFG distinction is meaningless for it — everything it holds is just goods-in-storage
  // owned by a Client, dispatchable regardless of type. This auto-widens the scope for that case
  // even if DispatchMaterialScope itself is still "FG" (the manual per-company setting a
  // Production-enabled company would actually rely on to restrict dispatch to finished output).
  const isPure3plNoProduction = thirdPartyLogisticsEnabled && !(processConfiguration?.productionModuleEnabled ?? true);
  const isAllMaterialScope = processConfiguration?.dispatchMaterialScope === 'All' || isPure3plNoProduction;
  const dispatchableMaterials = isAllMaterialScope ? materials : materials.filter((m) => m.materialType === 'FG');

  const resetForm = () => {
    setEditingId(null);
    setDestinationType(showCustomerOption ? 'Customer' : 'Warehouse');
    setCustomerId('');
    setDestinationWarehouseId('');
    setWarehouseId('');
    setClientId('');
    setRemarks('');
    setLines([EMPTY_LINE]);
  };

  // Only usable while Status is still "Draft" — mirrors DispatchOrderService.UpdateAsync's guard.
  const openEdit = (r: DispatchOrderDto) => {
    setEditingId(r.id);
    setDestinationType(r.destinationType);
    setCustomerId(r.customerId ?? '');
    setDestinationWarehouseId(r.destinationWarehouseId ?? '');
    setWarehouseId(r.warehouseId);
    setClientId(r.clientId ?? '');
    setRemarks(r.remarks ?? '');
    setLines(r.lines.map((l) => ({ materialId: l.materialId, requestedQty: l.requestedQty })));
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['dispatch-orders'] });

  const cleanLines = lines.filter((l) => l.materialId && l.requestedQty > 0);

  const createMutation = useMutation({
    mutationFn: () => {
      const resolvedClientId = processConfiguration?.thirdPartyLogisticsEnabled ? clientId || null : null;
      const destinationFields = destinationType === 'Warehouse'
        ? { destinationType: 'Warehouse' as const, customerId: null, destinationWarehouseId }
        : { destinationType: 'Customer' as const, customerId, destinationWarehouseId: null };
      if (editingId) {
        const payload: UpdateDispatchOrderRequest = {
          warehouseId, orderDate: null, remarks: remarks || null, lines: cleanLines, clientId: resolvedClientId, ...destinationFields,
        };
        return apiClient.put(`/dispatch-orders/${editingId}`, payload);
      }
      const payload: CreateDispatchOrderRequest = {
        warehouseId, orderDate: null, remarks: remarks || null, lines: cleanLines, clientId: resolvedClientId, ...destinationFields,
      };
      return apiClient.post('/dispatch-orders', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/dispatch-orders/${id}/release`),
    // Also invalidates dispatch-pick-lists — when Dispatch Pick List Generation is "Auto", release
    // generates the pick list server-side (see DispatchOrderService.ReleaseAsync), so the Dispatch
    // Pick List screen needs a fresh fetch to actually show it.
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['dispatch-pick-lists'] });
    },
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/dispatch-orders/${id}/close`),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<DispatchOrderDto>[] = [
    { field: 'orderNumber', headerName: t('table.number') },
    {
      field: 'customerName', headerName: t('table.customer'), filterable: true,
      render: (r) => (r.destinationType === 'Warehouse' ? t('table.toWarehouse', { warehouse: r.destinationWarehouseName ?? '—' }) : r.customerName ?? '—'),
    },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => (r.isClosed ? <StatusChip status="Closed" /> : <StatusChip status={r.status} />), filterable: true },
    ...(processConfiguration?.thirdPartyLogisticsEnabled
      ? [{ field: 'clientName', headerName: t('table.client'), render: (r: DispatchOrderDto) => r.clientName ?? '—', filterable: true } as DataTableColumn<DispatchOrderDto>]
      : []),
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.DISPATCH_ORDER_MANAGE}>
            {r.status === 'Draft' && (
              <>
                <Button size="small" onClick={() => openEdit(r)}>
                  {t('edit')}
                </Button>
                <Button size="small" variant="outlined" disabled={releaseMutation.isPending} onClick={() => releaseMutation.mutate(r.id)}>
                  {t('release')}
                </Button>
              </>
            )}
            {r.status !== 'Completed' && !r.isClosed && (
              <Button size="small" color="error" disabled={closeMutation.isPending} onClick={() => closeMutation.mutate(r.id)}>
                {t('close')}
              </Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<DispatchOrderLineDto>[] = [
    { field: 'materialCode', headerName: t('table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'materialType', headerName: t('table.type'), render: (l) => l.materialType ?? '—' },
    { field: 'requestedQty', headerName: t('table.requestedQty') },
    { field: 'issuedQty', headerName: t('table.issuedQty') },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.DISPATCH_ORDER_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={orders} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="dispatch-orders" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Both offered only when Process Configuration's Dispatch Type is "Both" — a company
                scoped to just one destination type never sees a choice to make at all, the flow
                stays exactly as fixed as it always was for "Customer"-only companies. */}
            {showCustomerOption && showWarehouseOption && (
              <ToggleButtonGroup
                exclusive size="small" value={destinationType}
                onChange={(_, v: 'Customer' | 'Warehouse' | null) => v && setDestinationType(v)}
                sx={{ alignSelf: 'flex-start' }}
              >
                <ToggleButton value="Customer">{t('form.destinationCustomer')}</ToggleButton>
                <ToggleButton value="Warehouse">{t('form.destinationWarehouse')}</ToggleButton>
              </ToggleButtonGroup>
            )}
            {destinationType === 'Warehouse' ? (
              <SearchableSelect
                label={t('form.destinationWarehouseField')} value={destinationWarehouseId} onChange={setDestinationWarehouseId} fullWidth
                helperText={t('form.destinationWarehouseHint')}
                // Excludes the source warehouse below — dispatching a warehouse to itself isn't a
                // real move (mirrors the server's own ResolveDestination check).
                options={warehouses.filter((w) => w.id !== warehouseId).map((w) => ({ value: w.id, label: w.name }))}
              />
            ) : (
              <SearchableSelect
                label={t('form.customer')} value={customerId} fullWidth
                onChange={(next) => {
                  setCustomerId(next);
                  // Clear an already-picked Client if it's no longer mapped to the newly-picked
                  // Customer — same "reset dependent field when it's no longer valid" treatment used
                  // throughout this app for cascading pickers.
                  const stillValid = clients.find((c) => c.id === clientId)?.customerIds.includes(next);
                  if (!stillValid) setClientId('');
                }}
                options={customers.map((c) => ({ value: c.id, label: c.name }))}
              />
            )}
            <SearchableSelect
              label={t('form.warehouse')} value={warehouseId}
              onChange={(next) => {
                setWarehouseId(next);
                // The destination warehouse must differ from the source — clear it if the operator
                // just changed the source to match what was picked as the destination.
                if (next === destinationWarehouseId) setDestinationWarehouseId('');
              }}
              fullWidth
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
            {processConfiguration?.thirdPartyLogisticsEnabled && (
              <SearchableSelect
                label={t('form.client')} value={clientId} onChange={setClientId} fullWidth
                disabled={!customerId}
                helperText={customerId ? undefined : t('form.clientHelperNeedsCustomer')}
                // Union with the already-assigned clientId so editing an older order never shows a
                // blanked-out Client field even if the mapping changed since it was created.
                options={clients.filter((c) => c.customerIds.includes(customerId) || c.id === clientId).map((c) => ({ value: c.id, label: c.name }))}
              />
            )}
            <TextField label={t('form.remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />

            <Divider />

            <Typography variant="subtitle2">{isAllMaterialScope ? t('form.linesAll') : t('form.lines')}</Typography>
            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={isAllMaterialScope ? t('form.materialAll') : t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(value) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId: value } : l)))}
                  options={dispatchableMaterials.map((m) => ({ value: m.id, label: m.materialType ? `${m.code} — ${m.name} (${m.materialType})` : `${m.code} — ${m.name}` }))}
                />
                <TextField
                  type="number" label={t('form.quantity')} value={line.requestedQty} size="small" sx={{ minWidth: 120 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, requestedQty: Number(e.target.value) } : l)))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])} sx={{ alignSelf: 'flex-start' }}>
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={(destinationType === 'Warehouse' ? !destinationWarehouseId : !customerId) || !warehouseId || cleanLines.length === 0 || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.orderNumber ?? ''}
        subtitle={viewing
          ? `${viewing.destinationType === 'Warehouse' ? `→ ${viewing.destinationWarehouseName}` : viewing.customerName} · ${viewing.warehouseName}`
          : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
